import * as c from 'models/constants';
import { translate } from 'react-i18next';
import { FieldArray } from 'redux-form';
import { formatCurrency, formatDuration } from 'helpers/common';
import renderIcon from 'helpers/renderIcon';
import { FieldArrayFieldsProps, ITranslateProps } from 'models/common';
import React = require('react');
import { Link } from 'react-router';
import {
	SortableHandle,
	SortableElementProps,
	SortableElement,
	SortableContainerProps,
	SortableContainer,
	SortEndHandler,
} from 'react-sortable-hoc';
import { PortalPageSection } from 'components/PortalStructure';
import { Tooltip } from 'components';
import { IServiceGroup, IServiceTree, IServiceTreeServiceItem } from 'models/shop/serviceGroups';

interface IForwardedProps {
	shopId: string;
	officeId: string;
	activeDifferentGroup: IServiceGroup['id'];
	editMode: boolean;
	currencyKey?: number;
	categoryIndex: number;
	isServiceGroupBeingDragged?: boolean;
	isServiceCategoryBeingDragged?: boolean;
	isServiceCategorySortMode?: boolean;
	isServiceGroupSortMode?: boolean;
	setActiveGroup: (c: IServiceGroup['id']) => void;
	startSortingService: (s: Pick<IServiceGroup, 'id' | 'parentId'>) => void;
	endSortingService: (a: Parameters<SortEndHandler>[0], c: Pick<IServiceGroup, 'id' | 'parentId'>, key: number) => void;
	startSortingServiceGroup: () => void;
	endSortingServiceGroup?: (a: Parameters<SortEndHandler>[0], key: number) => void;
	openAddEditServiceGroup: (serviceGroupData: IServiceTree & IServiceGroup) => void;
}

/****** HANDLE ******/

const DragHandle = SortableHandle(() => (
	<div className="dragIconContainer d-flex flex-column justify-content-between">{renderIcon('hamburger')}</div>
));

/****** SERVICE ITEM ******/

interface IServiceItemOwnProps extends IForwardedProps, SortableElementProps {
	index: number;
	item: IServiceTree['children'][number];
	prefix: string;
}

const isService = (c: IServiceTree['children'][number]): c is IServiceTreeServiceItem => Array.isArray(c);
const getServicesList = (child: IServiceTree['children'][number]): IServiceTreeServiceItem => {
	if (!child) {
		return [];
	}
	return isService(child)
		? child
		: child.children?.reduce<IServiceTreeServiceItem>((acc, val) => [...acc, ...getServicesList(val)], []);
};
type IServiceItemProps = IServiceItemOwnProps & IForwardedProps & ITranslateProps;
const getChildName = (item: IServiceTree['children'][number]): string =>
	isService(item) ? getServicesList(item)[0]?.name : item.value;
const ProtoServiceListItem: React.ComponentType<IServiceItemProps> = (props) => {
	const { item, shopId, officeId, t, currencyKey } = props;

	const realServices = getServicesList(item);
	const first = realServices[0];
	const containsUnpublishedServices = !isService(item) && realServices.some((x) => !x.published);
	let hasResources: boolean = true; // because of subservices set to true for now
	let children: IServiceTree['children'] = isService(item) ? [] : item.children || [];
	let singleMinDuration = null;
	let singleMinPrice = null;
	const flattenedArray = [].concat.apply([], children);

	// Filter out variations to display only one of them
	const filteredFlattenedArray = flattenedArray.reduce((acc, cur) => {
		if (!acc.find((item) => item.variantParentId === cur.variantParentId)) {
			acc.push(cur);
		}

		return acc;
	}, []);

	// Sort subservices by order
	const sortedFilteredFlattenedArray = [...filteredFlattenedArray].sort((a, b) => a.order - b.order);

	const name = getChildName(item);

	// If a single child service has the same name, don't show it!
	if (children.length === 1 && isService(children[0]) && getChildName(children[0]) === name) {
		// Check if the normal service has resources
		const normalService: any = children[0][0];

		// Change once custom data is used for normal services and not only bundles
		// Don't display "no resources" icon for bundles (we can't know if the children bookings have resources set on them here)
		if (!normalService.customData) {
			hasResources = normalService.resources.length ? true : false;
		}

		children = [];

		// Set min duration and min price for single service with variations
		if (realServices.length > 1) {
			singleMinDuration = realServices.reduce((prev, curr) => (prev.duration < curr.duration ? prev : curr));
			singleMinPrice = realServices.reduce((prev, curr) => (prev.originalPrice < curr.originalPrice ? prev : curr));
		}
	} else if (children.length > 1) {
		// Check if a service with subservices has all their resources set
		const allHaveResources = !flattenedArray.some((obj) => Array.isArray(obj.resources) && obj.resources.length === 0);
		hasResources = allHaveResources;
	}

	const key = isService(item) ? first?.variantParentId || Math.random() : item.id;

	// TODO: Build link with serviceGroupId when editing variants is implemented
	let editLink = '';

	if (first && 'parentId' in item) {
		// use the following condition instead when editing variants is implemented (hides edit button on variants)
		// if (first && !isService(item)) {
		let isBundle = false;

		if (first.customData) {
			const customDataParsed = JSON.parse(first.customData);
			isBundle = customDataParsed.isBundle;
		}

		if (isBundle) {
			editLink = `/shop/${shopId}/${officeId}/edit/bundle/existing/${first.variantParentId}/${first.serviceGroupId}`;
		} else {
			editLink = `/shop/${shopId}/${officeId}/edit/services/existing/${first.variantParentId}/${first.serviceGroupId}`;
		}
	}

	// For bundles
	let childrenBookingTemplatesNames = '';

	if (first && first?.customData && JSON.parse(first.customData).isBundle) {
		const customDataParsed = JSON.parse(first.customData);

		customDataParsed.bundleChildrenNames.map((name, idx) => {
			if (idx === 0) {
				childrenBookingTemplatesNames = name;
			} else {
				childrenBookingTemplatesNames = childrenBookingTemplatesNames + ', ' + name;
			}
		});
	}

	return (
		<React.Fragment key={key}>
			<div className="serviceCardHolder d-flex align-items-center">
				{isService(item) ? null : props.editMode ? <DragHandle /> : null}
				<Link to={editLink}>
					<PortalPageSection>
						<div className="serviceCardEdit">
							{children.length ? (
								<div className="container px-0">
									<div className="row serviceName__row">
										<div className="col-12">
											<div className="serviceName__nameContainer">
												{hasResources ? null : (
													<Tooltip message={t('User_ShopCard_Services_ServiceNoResources')}>
														{renderIcon('warning')}
													</Tooltip>
												)}
												{containsUnpublishedServices ? (
													<Tooltip message={t('User_ShopCard_Services_ServiceManually')}>
														{renderIcon('hidden')}
													</Tooltip>
												) : null}
												<span className={`serviceName__name ${props?.editMode ? 'editServiceName' : ''}`}>{name}</span>
											</div>
										</div>
									</div>
									{sortedFilteredFlattenedArray.map((subservice) => {
										// Map through flattenedArray instead if you want to display subservices with variations
										const variationsArray = flattenedArray.filter(
											(ser) => ser.variantParentId === subservice.variantParentId,
										);
										const minDuration = variationsArray.reduce((prev, curr) =>
											prev.duration < curr.duration ? prev : curr,
										);
										const minPrice = variationsArray.reduce((prev, curr) =>
											prev.originalPrice < curr.originalPrice ? prev : curr,
										);

										return (
											<div className="row align-items-center" key={subservice.id}>
												<div className="col-lg-6 col-md-12 col-12">
													<div className={`subServiceName__name ${props?.editMode ? 'editServiceName' : ''}`}>
														{subservice.name}
													</div>
												</div>
												<div className="col-lg-3 col-md-6 col-6">
													<div className="subServiceName__duration">
														{variationsArray.length > 1 ? (
															<>
																<span>{t('BSPortal_Common_From').toLowerCase()}</span>{' '}
																{formatDuration(minDuration.duration)}
															</>
														) : (
															formatDuration(subservice.duration)
														)}
													</div>
												</div>
												<div className="col-lg-3 col-md-6 col-6">
													<div className="subServiceName__price">
														{variationsArray.length > 1 ? (
															<>
																<span>{t('BSPortal_Common_From').toLowerCase()}</span>{' '}
																{formatCurrency(currencyKey, minPrice.originalPrice)}
															</>
														) : (
															formatCurrency(currencyKey, subservice.originalPrice)
														)}
													</div>
												</div>
											</div>
										);
									})}
								</div>
							) : (
								<div className="container px-0">
									<div className="row align-items-center">
										<div className="col-lg-6 col-md-12 col-12">
											<div className="serviceName__nameContainer">
												{hasResources ? null : (
													<Tooltip message={t('User_ShopCard_Services_ServiceNoResources')}>
														{renderIcon('warning')}
													</Tooltip>
												)}
												{containsUnpublishedServices ? (
													<Tooltip message={t('User_ShopCard_Services_ServiceManually')}>
														{renderIcon('hidden')}
													</Tooltip>
												) : null}
												<span className={`serviceName__name ${props?.editMode ? 'editServiceName' : ''}`}>{name}</span>
											</div>
										</div>
										{first && first?.customData && JSON.parse(first.customData).isBundle ? (
											<div className="col-12 bundleService__namesContainerMobile">
												<div className="bundleService__namesContainer">
													<p className="bundleService__names">{childrenBookingTemplatesNames}</p>
												</div>
											</div>
										) : null}
										<div className="col-lg-3 col-md-6 col-6">
											<div className="serviceName__duration">
												{first && realServices.length > 1 ? (
													<>
														<span>{t('BSPortal_Common_From').toLowerCase()}</span>{' '}
														{formatDuration(singleMinDuration.duration)}
													</>
												) : (
													formatDuration(first?.duration)
												)}
											</div>
										</div>
										<div className="col-lg-3 col-md-6 col-6">
											<div className="serviceName__price">
												{first && realServices.length > 1 ? (
													<>
														<span>{t('BSPortal_Common_From').toLowerCase()}</span>{' '}
														{formatCurrency(currencyKey, singleMinPrice.originalPrice)}
													</>
												) : (
													formatCurrency(currencyKey, first?.originalPrice)
												)}
											</div>
										</div>
										{first && first?.customData && JSON.parse(first.customData).isBundle ? (
											<div className="col-12 bundleService__namesContainerDesktop">
												<div className="bundleService__namesContainer">
													<p className="bundleService__names">{childrenBookingTemplatesNames}</p>
												</div>
											</div>
										) : null}
									</div>
								</div>
							)}
						</div>
					</PortalPageSection>
				</Link>
			</div>
		</React.Fragment>
	);
};
const ServiceListItem: React.FC<IServiceItemOwnProps> = translate(c.DEFAULT_TRANSLATION_NAMESPACE)(
	SortableElement(ProtoServiceListItem),
);

/****** SERVICE LIST ******/

interface IServiceListOwnProps extends IForwardedProps, SortableContainerProps {
	prefix: string;
	items: IServiceTree['children'];
}
interface IServiceListProps extends IServiceListOwnProps, ITranslateProps {}
class ProtoServiceList extends React.Component<IServiceListProps> {
	renderServices = ({ fields }: { fields: FieldArrayFieldsProps<IServiceTree['children'][number]> }): JSX.Element => (
		<div className="categoryList">
			{fields.map((itemPrefix, index) => {
				const item = fields.get(index);
				if (!item) {
					return null;
				}
				return (
					<ServiceListItem
						{...this.props}
						index={index}
						key={isService(item) ? item[0].variantParentId : item.id}
						prefix={itemPrefix}
						item={item}
						currencyKey={this.props.currencyKey}
					/>
				);
			})}
		</div>
	);
	render() {
		return <FieldArray name={this.props.prefix} component={this.renderServices} />;
	}
}
const ServiceList: React.FC<IServiceListOwnProps> = translate(c.DEFAULT_TRANSLATION_NAMESPACE)(
	SortableContainer(ProtoServiceList),
);

/****** CATEGORY ITEM ******/

interface ICategoryItemOwnProps extends IForwardedProps, SortableElementProps {
	prefix: string;
	selectedServiceCategory: number;
	ownIndex: number; // normal index prop is not forwarded inside sortable
	group: IServiceTree;
	sortable: boolean;
	fields: FieldArrayFieldsProps<any>;
}
interface ICategoryItemProps extends ICategoryItemOwnProps, ITranslateProps {}
const ProtoCategoryListItem: React.ComponentType<ICategoryItemProps> = (props) => {
	const [serviceGroupOptionsOpen, setServiceGroupOptionsOpen] = React.useState(false);
	const closeCallback = React.useRef<(this: Document, ev: MouseEvent) => any>();

	// close modal if user clicks anywhere
	React.useEffect(() => {
		if (closeCallback.current) {
			document.removeEventListener('click', closeCallback.current);
		}

		const closeModalCallback = () => {
			if (serviceGroupOptionsOpen) {
				setServiceGroupOptionsOpen(false);
				document.removeEventListener('click', closeModalCallback);
			}
		};

		closeCallback.current = closeModalCallback;

		if (serviceGroupOptionsOpen) {
			document.addEventListener('click', closeModalCallback);
		}

		return () => closeCallback.current && document.removeEventListener('click', closeCallback.current);
	}, [serviceGroupOptionsOpen]);

	const serviceGroupHeaderElement: JSX.Element = (
		<div className="d-flex flex-row align-items-center serviceGroupHeader">
			{props.editMode && props.sortable ? <DragHandle /> : null}
			<div className="serviceGroupHeader__nameOptionsContainer">
				{props.group.id ? (
					<div className="serviceGroupHeader__nameOptionsContainer__serviceGroupNameContainer">
						<div
							className="serviceGroupHeader__nameOptionsContainer__color"
							style={{ backgroundColor: props.group.color ? props.group.color : '#EA3546' }}
						></div>
						<span className="serviceGroupHeader__nameOptionsContainer__name">{props.group.value}</span>
						<span className="serviceGroupHeader__nameOptionsContainer__servicesNumber">
							({props.group.children.length})
						</span>
					</div>
				) : null}
				<div className="serviceGroupHeader__serviceGroupOptions">
					<div
						onClick={() => {
							setServiceGroupOptionsOpen(!serviceGroupOptionsOpen);
						}}
					>
						{renderIcon('moreHorizontal')}
					</div>
					{serviceGroupOptionsOpen && (
						<div className="servicesList__optionsHolder__serviceGroupSettingsContainer">
							<Link
								to={`/shop/${props.shopId}/${props.officeId}/choose-service?${c.SERVICE_CATEGORY}=${props.selectedServiceCategory}&${c.SERVICE_GROUP}=${props.group.id}&${c.SERVICE_LAST_ORDER_NUMBER}=${props.group.children.length}`}
							>
								<div className="servicesList__optionsHolder__serviceGroupSettingsContainer__text">
									{props.t('User_ShopCard_Services_AddNewService')}
								</div>
							</Link>
							<div
								onClick={() => props.openAddEditServiceGroup(props.group)}
								className="servicesList__optionsHolder__serviceGroupSettingsContainer__text"
							>
								{props.t('User_ShopCard_Services_EditServiceGroup')}
							</div>
							<div
								onClick={() => props.fields.remove(props.ownIndex)}
								className="servicesList__optionsHolder__serviceGroupSettingsContainer__text__delete"
							>
								{props.t('User_ShopCard_Services_Group_Delete_Button')}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);

	if (props.isServiceGroupSortMode) {
		return serviceGroupHeaderElement;
	}

	let tree: JSX.Element;

	if (props.group.children.length) {
		tree = (
			<ServiceList
				{...props}
				prefix={props.prefix + '.children'}
				items={props.group.children}
				onSortStart={() => props.startSortingService(props.group)}
				onSortEnd={(a) => props.endSortingService(a, props.group, props.selectedServiceCategory)}
				axis="y"
				lockAxis="y"
				useWindowAsScrollContainer={true}
				useDragHandle={true}
			/>
		);
	}

	let className = 'servicesCategory';

	if (props.activeDifferentGroup === props.group.id) {
		className += ' is-active';
	}

	if (props.sortable) {
		className += ' sortable';
	}

	const servicesList: JSX.Element = <div className="serviceListHolder">{tree}</div>;

	return (
		<div onMouseOver={() => props.setActiveGroup(props.group.id)} className={className}>
			{serviceGroupHeaderElement}
			{servicesList}
		</div>
	);
};
const CategoryListItem: React.FC<ICategoryItemOwnProps> = translate(c.DEFAULT_TRANSLATION_NAMESPACE)(
	SortableElement(ProtoCategoryListItem),
);

/****** CATEGORY LIST ******/

interface IServiceGroupListOwnProps extends IForwardedProps, SortableContainerProps {
	prefix: string;
	selectedServiceCategory: number;
}
interface IServiceGroupListProps extends IServiceGroupListOwnProps, ITranslateProps {}
class ProtoGroupsList extends React.Component<IServiceGroupListProps> {
	renderGroups = ({ fields }: { fields: FieldArrayFieldsProps<IServiceTree> }): JSX.Element => {
		return (
			<div className="categoryList">
				{fields.map((c, index) => (
					<CategoryListItem
						{...this.props}
						index={index}
						ownIndex={index}
						key={fields.get(index).id || index}
						fields={fields}
						prefix={c}
						group={fields.get(index)}
						sortable={fields.length > 1}
						selectedServiceCategory={this.props.selectedServiceCategory}
					/>
				))}
			</div>
		);
	};

	render() {
		return <FieldArray name={this.props.prefix} component={this.renderGroups} />;
	}
}
const ServiceGroupList: React.FC<IServiceGroupListOwnProps> = translate(c.DEFAULT_TRANSLATION_NAMESPACE)(
	SortableContainer(ProtoGroupsList),
);

interface IServiceCategoryOwnProps extends IForwardedProps, SortableElementProps {
	prefix: string;
	selectedServiceCategory: number;
}
interface IServiceCategoryProps extends IServiceCategoryOwnProps, ITranslateProps {}

class ProtoServiceCategory extends React.Component<IServiceCategoryProps> {
	render() {
		if (this.props.isServiceCategorySortMode) {
			return (
				<div>
					<div className="categoryList__titleContainer d-flex flex-row align-items-center">
						{this.props.editMode ? <DragHandle /> : null}
						<h2 key={`title${this.props.selectedServiceCategory}`} className="categoryList__title">
							{this.props.t(`DV_ServiceCategories_${this.props.selectedServiceCategory}`)}
						</h2>
					</div>
				</div>
			);
		} else {
			return (
				<div>
					<div className="categoryList__titleContainer d-flex flex-row align-items-center">
						{this.props.editMode ? <DragHandle /> : null}
						<h2 key={`title${this.props.selectedServiceCategory}`} className="categoryList__title">
							{this.props.t(`DV_ServiceCategories_${this.props.selectedServiceCategory}`)}
						</h2>
					</div>
					<ServiceGroupList
						{...this.props}
						updateBeforeSortStart={() => this.props.startSortingServiceGroup()}
						onSortEnd={(a) => this.props.endSortingServiceGroup(a, this.props.categoryIndex)}
						axis="y"
						lockAxis="y"
						useWindowAsScrollContainer={true}
						useDragHandle={true}
					/>
				</div>
			);
		}
	}
}
const ServiceCategoryList: React.FC<IServiceCategoryProps> = translate(c.DEFAULT_TRANSLATION_NAMESPACE)(
	SortableElement(ProtoServiceCategory),
);

export { ServiceCategoryList };

interface IAllServiceCategoriesOwnProps extends SortableContainerProps, Partial<IForwardedProps> {
	allServiceCategoriesTree: JSX.Element[];
}
class ProtoAllServiceCategories extends React.Component<IAllServiceCategoriesOwnProps> {
	renderCategories = (): JSX.Element => (
		<div className="categoryList">{this.props.allServiceCategoriesTree.map((item) => item)}</div>
	);

	render() {
		return this.renderCategories();
	}
}
const AllServiceCategories: React.FC<IAllServiceCategoriesOwnProps> = translate(c.DEFAULT_TRANSLATION_NAMESPACE)(
	SortableContainer(ProtoAllServiceCategories),
);

export { AllServiceCategories };
